import React, { useState, useLayoutEffect, useRef, useEffect } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useDomainNavigate } from "domain";
import { useRateClasses } from './RateClassAPIEndpoints';
import { getWholeRow } from '../../../lib/DataGridHelpers';
import { UcPagination } from 'components/common/UcPagination';
import { UcIconButtons } from 'components/common/UcIconButtons';
import { useEnums } from 'components/auth/PermissionApiEndpoint';
import { enumFormatter } from 'lib/EnumFormatter';
import { useTranslation } from "react-i18next";
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete';
import ListIcon from '@mui/icons-material/List';
import { routePaths } from 'routes';
import { Chip } from '@mui/material';

export const RateClassGrid = (props) => {
    const { t } = useTranslation();
    const initialSort = [{ field: "name", sort: "asc" }];
    const sort = props.sort || initialSort;
    const initialDataState = props.dataState || { skip: 0, take: 10, };
    const navigate = useDomainNavigate();

    const [filter, setFilter] = useState({
        take: initialDataState.take,
        skip: initialDataState.skip,
        sort: sort,
        generalSearch: props.generalSearch,
    });

    useEffect(() => {
        if (props.generalSearch !== filter.generalSearch) {
            setFilter({ ...filter, generalSearch: props.generalSearch });
        }
    }, [filter, setFilter, props.generalSearch]);

    let initialFilterModel = {
        items: [
            { columnField: 'id', operatorValue: 'contains', value: '' }
        ],
        linkOperator: 'and',
    };

    const [filterModel] = useState(initialFilterModel);

    const needsDataUpdate = useRef("");
    const rateStatusEnums = useEnums("RateStatus");
    const commodityEnums = useEnums("Commodity");

    let defaultDataItems = {
        columnVisibilityModel: {
            id: false,
            cepOrgId: false,
            content: false,
            createdBy: false,
            updatedBy: false,
            meterCount: false,
        },
        columns: [
            {
                field: 'details', headerName: '', width: 50,
                valueGetter: getWholeRow,
                renderCell: () => {
                    const actions = [
                        {
                            title: t('Open Meter Rates'),
                            icon: <ListIcon data-cy-id={"rateClassDetailsButton"} />,
                            color: 'primary',
                            isHidden: false,
                            isDisabled: false,
                            requiredPermission: 'RateClass:Read',
                            onClick: () => {
                                props.setDrawerOpen(true);
                            },
                        },
                    ];
                    return (
                        <UcIconButtons actions={actions} />
                    );
                }
            },
            { field: 'name', headerName: t('Name'), width: 200 },
            { field: 'meterCount', headerName: t('Meter Total'), width: 100 },
            { field: 'description', headerName: t('Description'), flex: 1, minWidth: 200 },
            { field: 'status', headerName: t('Status'), width: 150,
                renderCell: (params) => {
                    return (
                        <>
                            <Chip
                                label={params.value}
                                color={enumFormatter(rateStatusEnums, params.value).toLowerCase() === 'active' ? "success" : "error"}
                                variant="outlined"
                            />
                        </>
                    );
                }
            },
            { field: 'commodity', headerName: t('Commodity'), width: 150, valueFormatter: ( params ) => {return enumFormatter(commodityEnums, params.value);} },
            { field: 'u1020Key', headerName: t('Rate Class Code'), width: 150 },
            {
                field: 'allowComparison', headerName: t('Rate Comparison'), width: 150,
                renderCell: (params) => {
                    return (                     
                        <Chip
                            label={params.value ? "Enabled" : "Disabled"}
                            color={params.value ? "success" : "warning"}
                            variant="outlined"
                        />
                    );
                }
            },
            { field: 'icons', headerName: '',
                valueGetter: getWholeRow,
                renderCell: (params) => {
                    const actions = [
                        {
                            title: t('Edit Rate Class'),
                            icon: <EditIcon data-cy-id={"rate_class_edit_" + params.row.name}/>,
                            color: 'primary',
                            isHidden: false,
                            isDisabled: false,
                            requiredPermission: 'RateClass:Modify',
                            onClick: () => navigate(routePaths.rateVersionListPage.getPath(params.id, params.value.name))
                        },
                        {
                            title: t('Delete Rate Class'),
                            icon: <DeleteIcon data-cy-id={"rate_class_delete_" + params.row.name}/>,
                            color: 'error',
                            isHidden: false,
                            isDisabled: false,
                            requiredPermission: 'RateClass:Delete',
                            onClick: () => { props.onDeleteItem(params.value); },
                        }
                    ];

                    return (
                        <UcIconButtons actions={actions} />
                    );
                }
            },
        ],
        rows: [],
        rowCount: -1,
    };
    const [dataItems, setDataItems] = useState(defaultDataItems);

    const defaultSelectedItem = { dataItem: {}, selectedId: 0 };
    const [selectedItem, setSelectedItem] = useState(defaultSelectedItem);

    const isDataLoaded = useRef(false);

    const dataReceived = (swrResult) => {
        let receivedDataItems = swrResult.result;

        let selectedDataItems = receivedDataItems.map((item) => {
            return { ...item, selected: item.id === selectedItem.selectedId };
        });
        selectedDataItems.iteration = receivedDataItems.iteration;
        selectedDataItems.rowCount = swrResult.rowCount;
        if (selectedDataItems.length > selectedDataItems.rowCount) {
            selectedDataItems.rowCount = selectedDataItems.length;
        }

        setDataItems({ ...defaultDataItems, rows: [...receivedDataItems], rowCount: swrResult.rowCount });
    };

    const swrRateClasses = useRateClasses(filter);
    const isLoading = swrRateClasses.isLoading || !isDataLoaded.current;
    if (!swrRateClasses.result && defaultDataItems.rowCount !== -1) {
        setDataItems(defaultDataItems);
    }

    useLayoutEffect(() => {
        if (swrRateClasses.result) {
            isDataLoaded.current = true;
            let filterString = JSON.stringify(filter);
            if (needsDataUpdate.current !== filterString) {
                needsDataUpdate.current = filterString;
                dataReceived(swrRateClasses);
            }
        }
    });

    const updateFilter = (newFilter) => {
        setFilter(newFilter);
        props.onFilterChange(newFilter);
    };

    console.log("RateClassGrid - Loaded");
    return (
        <div
            data-cy-id={"rateClassGridWrapper"}
        >
            <DataGridPro
                autoHeight={true}
                disableColumnFilter={true}
                disableColumnSelector={true}
                loading={isLoading}

                sortingMode='server'

                filterMode='server'
                sortModel={filter.sort}
                onSortModelChange={(model) => updateFilter({ ...filter, sort: model })}
                filterModel={filterModel}
                paginationModel={{page: filter.skip / filter.take, pageSize: filter.take}}
                onPaginationModelChange={(model) => updateFilter({ ...filter, skip: model.page * model.pageSize, take: model.pageSize })}nge={(newPageSize) => updateFilter({ ...filter, take: newPageSize })}

                paginationMode='server'
                rowsPerPageOptions={[5, 10, 20]}

                onRowSelectionModelChange={(newSelectionModel) => {
                    if (newSelectionModel.length !== 1) {
                        setSelectedItem(defaultSelectedItem);
                        props.onSelected(defaultSelectedItem.dataItem);
                        return;
                    }
                    const selectedId = newSelectionModel[0];
                    let selectedRateItem = dataItems.rows.filter(obj => { return obj.id === selectedId });
                    if (selectedRateItem.length !== 1) {
                        setSelectedItem(defaultSelectedItem);
                        props.onSelected(defaultSelectedItem.dataItem);
                        return;
                    }
                    selectedRateItem = selectedRateItem[0];
                    let newlySelectedItem = { dataItem: selectedRateItem, selectedId: selectedId };

                    setSelectedItem(newlySelectedItem);
                    props.onSelected(newlySelectedItem.dataItem);
                }}
                pagination
                components={{
                    Toolbar: () => {return  props.toolbar || <></>},
                    Pagination: UcPagination,
                }}
                {...dataItems}
                initialState={{ pinnedColumns: { right: ['icons'] } }}
            />
        </div>
    );
}

RateClassGrid.defaultProps = {
    onSelected: (selectedItem) => { console.log("onSelected not implemented", selectedItem); },
    onFilterChange: (filter) => { console.log("onFilterChange not implemented", filter); },
};
